/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */

import { IT_DASHBOARD } from "~/constants/routes";
import { userService } from "~/services";
import { store } from "~/store";
import { setUser, setUserPermissions, setUserPermissionsLoading } from "~/store/context/authSlice";
import { dashboardActions } from "~/store/context/dashboardSlice";
import { fetchSystemServices, fetchSystemSettings } from "~/store/context/settingsSlice";
import { selectWebSocketStatus, utilityActions } from "~/store/context/utilitySlice";
import evt from "~/store/event";
import { connectSocket } from "~/store/middleware/eventsWebsocket";
import { observeStoreChange } from "~/store/utils";

function onAppStart() {
  console.log("App started");
  // initiate web socket connection
  store.dispatch(connectSocket())

  store.dispatch(fetchSystemSettings());
  store.dispatch(fetchSystemServices());

  evt.emit("operations");
  // Get query params
  const params = new URLSearchParams(window.location.search);
  const startTime = params.get("startTime");
  const endTime = params.get("endTime");
  const interval = params.get("interval");

  if (window.location?.pathname === "/") {
    if (startTime) {
      store.dispatch(dashboardActions.setStartTime(Number(startTime)));
    }
    if (endTime) {
      store.dispatch(dashboardActions.setEndTime(Number(endTime)));
    }
    if (interval) {
      store.dispatch(dashboardActions.setTimeInterval({ interval }));
    }
  } else {
    if (startTime) {
      store.dispatch(utilityActions.setStartTime(Number(startTime)));
    }
    if (endTime) {
      store.dispatch(utilityActions.setEndTime(Number(endTime)));
    }
    if (interval) {
      store.dispatch(utilityActions.setTimeInterval({ interval }));
    }
  }

  const aggregationPeriod = params.get("aggregationPeriod");
  if (aggregationPeriod) {
    store.dispatch(utilityActions.setAggregationPeriod(aggregationPeriod));
  }

  const id = params.get("id");
  if (id) {
    store.dispatch(utilityActions.setSelectedID(id));
  }

  // update timezone
  updateTimezone();

  // Update user permissions
  updateUserPermissions();
}

export default onAppStart;


export function fetchDataOnStart() {
  // fetch on startup
  if (location?.pathname !== IT_DASHBOARD) {
    evt.emit("refreshRadios"); // this refresh itself as it is bound to observe by radio simulator for the first time.
    evt.emit("refreshEdges");
    evt.emit("refreshEdgePools");
    evt.emit("refreshSites");
    evt.emit("refreshRadioGroups");
    evt.emit("refreshDeviceGroups");
    evt.emit("refreshDevices");
  }
  evt.emit("refreshServiceController");
  evt.emit("operations");
  // evt.emit("refreshInsights", "ACTIVE");
}

async function updateTimezone() {
  try {
    const userInfo = await userService.getUserInfo();
    if (!userInfo) return;
    // sync userInfo to store
    store.dispatch(setUser(userInfo));

    if (userInfo.timezone) {
      store.dispatch(utilityActions.setTimeZone(userInfo.timezone));
    }
  } catch (error) {
    console.log("Could not fetch user info for timezone", error);
  }
}

async function updateUserPermissions() {
  store.dispatch(setUserPermissionsLoading(true));
  try {
    const userPermissions = await userService.getUserPermissions();
    if (userPermissions) {
      store.dispatch(setUserPermissions(userPermissions));
    }
  } catch (error) {
    console.log("Could not fetch user permissions ", error);
  } finally {
    store.dispatch(setUserPermissionsLoading(false));
  }
}

// Emitted when the hidden-tab patch buffer overflowed - replaying it would be incomplete
evt.on("websocket:resync", fetchDataOnStart);

// Watch WebSocket status and fetch data periodically
setTimeout(() => {
  // Shared across status changes - observeStoreChange has no cleanup phase, so a
  // per-callback variable left the 10s refetch loop running forever after a WS drop
  let interval: NodeJS.Timeout | undefined;
  observeStoreChange(selectWebSocketStatus, (state) => {
    console.log("WebSocket status changed to", state);
    clearInterval(interval);
    interval = undefined;
    // if status is disconnected | reconnecting then fetch data periodically every 10 seconds
    if (state === "disconnected" || state === "reconnecting") {
      interval = setInterval(() => {
        console.log("Fetching data periodically for WebSocket status", state);
        fetchDataOnStart();
      }, 10 * 1000);
    }

    if (state === "connected") {
      fetchDataOnStart();
    }
  })
})